/**
 * ROOM CHOICE AT SELL PRICES — the shape only (18 Oct 2026). Pure zod, imported by `customer-pricing.ts` (the released
 * copy carries it as `pricing.room_choice`) and by `customer-proposal.ts` (the working draft carries it as `room_choice`).
 * The arithmetic and the words that explain it are in `room-choice.ts`.
 *
 * A SIBLING of the open-book `Offer`, deliberately not a `mode` on it: an open-book offer is built from COSTS (board,
 * underlay, a single labour rate, a margin) and every consumer of `Offer` reads them. This shape has no field that can
 * hold a cost, a margin, a labour rate or an underlay — each product carries ONE all-in sell rate per m² — so a cost
 * cannot reach the customer by omission. Same rule as `CustomerPricedLine`: it cannot leak what it cannot name.
 */
import { z } from 'zod';
const sell = z.number().finite().nonnegative().max(1e6);
const pt = z.object({ x: z.number().finite(), y: z.number().finite() });
export const RoomChoiceProduct = z.object({
    id: z.string().trim().min(1).max(80), name: z.string().trim().min(1).max(200),
    /** Selling language: what this floor is. */
    intro: z.string().max(400).default(''),
    /** ONE all-in SELL rate per measured m², EX GST: supply, install and preparation, margin already inside. */
    rate_per_m2: sell,
    /** The rooms this product may go in, by room id; null = any room on the offer. */
    rooms: z.array(z.string().max(80)).max(200).nullable().default(null),
    /**
     * CUTTING ALLOWANCE ON A BOARD (25 Oct 2026, Stephen Clarke P-1026-10306). Jacky: *"dont do this rate per measured m2, its $52 per
     * SQM thats how industry call it … CRM can add SQM wastage, default 5% over 50 SQM, 7% less then 50 SQM"*. The rate stays the
     * trade's rate per m² of board; the allowance is the fraction added to the measured m² (0.05, not 5), FROZEN here when the offer
     * is saved from the same `artemis_services.waste` rule the CRM product lines follow, so a released copy never moves. A
     * treatment (sand and polish) is 0; an offer saved before this field reads as 0 and prices exactly as it did.
     */
    cutting_allowance: z.number().finite().min(0).max(0.5).default(0),
    /**
     * THE BOARD'S PROFILE (Jacky, 26 Oct 2026: *"the profile of Bowood or whatever is selected need to show up … spec, the colour
     * option"*). Frozen from the catalogue when the offer is saved, so the page and the released copy show the same board: the
     * catalogue product it is, its photo, its typed spec in words, and the colours the customer may pick (the pick lives in
     * `CustomerSelection.colours[product.id]`). A treatment has none. Still no cost anywhere.
     */
    product_id: z.string().trim().max(160).nullable().default(null),
    hero: z.string().max(400).nullable().default(null),
    specs: z.array(z.object({ key: z.string().max(60), label: z.string().max(80), value: z.string().max(120) })).max(20).default([]),
    colours: z.array(z.object({ id: z.string().max(160), name: z.string().max(160), swatch: z.string().max(400).nullable().default(null) })).max(60).default([]),
    /** THE COLOUR VARIANT, when Artemis has settled it (26 Oct 2026, Minto J-1026-10296: *"proper register product & services …
     *  using CRM line of product linked to supplier"*). With `product_id` this is what the acceptance carries onto the quote's line
     *  and what the PO office orders; null = the colour is still the customer's or the office's to pick. */
    variant_id: z.string().trim().max(160).nullable().default(null),
});
export const RoomChoiceRoom = z.object({
    id: z.string().max(80), name: z.string().max(120), floor: z.string().max(80),
    /** The measured m² frozen from the bound canvas revision when the offer was saved. */
    area_m2: z.number().nonnegative().max(100000),
    /** For the plan when the released copy has no drawing of this floor. */
    polygon: z.array(pt).max(500).default([]),
    /** Why this room offers one product only — shown to the customer beside it. */
    pinned_reason: z.string().trim().max(300).nullable().default(null),
});
export const RoomChoiceGroup = z.object({ id: z.string().trim().min(1).max(60), name: z.string().trim().min(1).max(120), rooms: z.array(z.string().max(80)).min(1).max(200),
    /** The step's own words, read under its name before its rooms (Jacky, 26 Oct 2026: "lot of writing is cramped at bottom, not enough at the progress"). Selling language, data like the name. */
    intro: z.string().trim().max(600).default("") });
/**
 * A sell line that does not move with the choices (skirting, lifting, stairs — and included work outside the rooms, such as
 * Minto's whole first floor, 23 Oct 2026). `amount` = quantity × rate, to the nearest dollar. `note` is selling language
 * printed under it ("alternatives on request…"), data like the label: never translated, never a cost.
 */
export const RoomChoiceFixedLine = z.object({ id: z.string().trim().min(1).max(60), label: z.string().trim().min(1).max(200), quantity: z.number().finite().nonnegative().max(1e6), unit: z.string().trim().min(1).max(20), rate: sell, amount: sell, note: z.string().trim().max(300).default(''),
    /**
     * INCREMENTAL EXPOSURE (Jacky, 26 Oct 2026, Stephen Clarke P-1026-10306): *"remove dispose + new laminate is under 5K, great, then
     * lead into the known issue of flatness … safe budget of 4.4K … then finally starting from 7.3K to take care of existing solid
     * timber"* — and *"you dump 14K in front, likely the page will be closed"*. A fixed line may belong to a GROUP (by group id): the
     * page lists it on that group's card and the group's own total includes it, so a step reads as one bundle. `kind:'budget'` is a
     * forward-looking worst case (levelling after the lift) or a line priced later (a stain after the sample patch, rate 0): a
     * VARIATION in the customer pricing's sense — shown on its group's card under "only if needed", priced, agreed now, approved on
     * the day, and NEVER in any total (Jacky, 26 Oct 2026: *"the budget for levelling should not be added to total, its pending
     * variation only when needed, same as unknown for varnish"*). Absent on older offers: they render as before.
     */
    group: z.string().trim().max(60).nullable().default(null),
    kind: z.enum(['fixed', 'budget']).default('fixed') });
export const RoomChoiceOffer = z.object({
    products: z.array(RoomChoiceProduct).min(1).max(30),
    rooms: z.array(RoomChoiceRoom).min(1).max(200),
    groups: z.array(RoomChoiceGroup).max(30).default([]),
    /** The recommended product per room id — the selection the page opens on before the customer has chosen. */
    recommended: z.record(z.string(), z.string()),
    fixed_lines: z.array(RoomChoiceFixedLine).max(30).default([]),
    /**
     * HOW THE PROPOSAL CLOSES (Jacky, 26 Oct 2026, Stephen Clarke): *"remove the accept on page, just call Jacky and we will work with
     * you along your renovation schedule"*. `accept_on_page` is today's page (Accept button, the acceptance section, "press Accept" in
     * the footer and the email); `call` hides acceptance and says to call Jacky, on the page and in the email's close. The customer still
     * chooses board, colour and edge finish on the page, and EmmA still answers there. Absent on older offers: accept on the page.
     */
    closing: z.enum(['accept_on_page', 'call']).default('accept_on_page'),
}).superRefine((o, ctx) => {
    const roomIds = new Set(o.rooms.map(r => r.id)), productIds = new Set(o.products.map(p => p.id));
    const issue = (message) => ctx.addIssue({ code: 'custom', message });
    if (roomIds.size !== o.rooms.length)
        issue('Room ids must be unique');
    if (productIds.size !== o.products.length)
        issue('Product ids must be unique');
    for (const p of o.products)
        for (const id of p.rooms ?? [])
            if (!roomIds.has(id))
                issue(`${p.name} names a room that is not on the offer (${id})`);
    for (const g of o.groups)
        for (const id of g.rooms)
            if (!roomIds.has(id))
                issue(`Group ${g.name} names a room that is not on the offer (${id})`);
    const groupIds = new Set(o.groups.map(g => g.id));
    for (const f of o.fixed_lines)
        if (f.group && !groupIds.has(f.group))
            issue(`${f.label} belongs to a group that is not on the offer (${f.group})`);
    const allowed = (roomId) => o.products.filter(p => p.rooms === null || p.rooms.includes(roomId));
    for (const r of o.rooms) {
        const offered = allowed(r.id);
        if (!offered.length)
            issue(`${r.name} has no product offered`);
        if (r.pinned_reason && offered.length !== 1)
            issue(`${r.name} carries a pinned reason but offers ${offered.length} products`);
        const rec = o.recommended[r.id];
        if (!rec || !offered.some(p => p.id === rec))
            issue(`${r.name} needs a recommended product that is offered there`);
    }
});
//# sourceMappingURL=room-choice-offer.js.map